// BTC3 Phase 11 Stage A1 — reactive viewport breakpoint hook.
//
// The portal and public-site drawers previously read window.innerWidth
// inline during render to compute aria-hidden.  That value is only
// re-evaluated when the component itself re-renders for an unrelated
// reason, so a desktop resize from mobile left aria-hidden stale.
//
// This hook subscribes to a matchMedia list and keeps the component
// state in sync with the viewport.  The Tailwind `lg` breakpoint is
// 1024px, so a width >= 1024 is treated as desktop.
import { useEffect, useState } from "react";

const DESKTOP_QUERY = "(min-width: 1024px)";

function readInitial() {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia(DESKTOP_QUERY).matches;
}

export default function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(readInitial);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    // Modern browsers expose addEventListener; keep legacy Safari fallback.
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
    } else if (mql.addListener) {
      mql.addListener(onChange);
    }
    // Also listen to resize events as a defensive sync for
    // environments (e.g. Playwright) that programmatically set the
    // viewport without emitting a matchMedia change event.
    const onResize = () => setIsDesktop(window.matchMedia(DESKTOP_QUERY).matches);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    // Sync once on mount in case initial state missed a race.
    onResize();
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", onChange);
      } else if (mql.removeListener) {
        mql.removeListener(onChange);
      }
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  return isDesktop;
}
